import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { IconButton } from './Button';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}

export function Modal({ open, onClose, title, subtitle, children, footer, width = 720 }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6">
      <div
        className="absolute inset-0 animate-fade bg-black/25 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ maxWidth: width }}
        className={cn(
          'relative flex w-full flex-col overflow-hidden',
          'max-h-[92dvh] rounded-t-[18px] pb-[env(safe-area-inset-bottom)]',
          'sm:max-h-[86vh] sm:rounded-[18px] sm:pb-0',
          'animate-pop bg-[var(--bg-panel-solid)] shadow-[var(--shadow-lg)]',
          'ring-1 ring-[var(--hairline)]',
        )}
      >
        <header className="flex items-start justify-between gap-4 px-4 py-3.5 sm:px-5 sm:py-4 hairline-b">
          <div>
            <h2 className="text-[15px] font-semibold tracking-[-0.01em]">{title}</h2>
            {subtitle && (
              <p className="mt-0.5 text-[12px] text-[var(--text-secondary)]">{subtitle}</p>
            )}
          </div>
          <IconButton label="Close" onClick={onClose}>
            <X size={16} />
          </IconButton>
        </header>
        <div className="scroll-thin flex-1 overflow-y-auto">{children}</div>
        {footer && <footer className="px-4 py-3 sm:px-5 sm:py-3.5 hairline-t">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
